import { Fragment, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDebounce } from "use-debounce";
import Header from "../../components/Header/Header";
import { searchArtists } from "../../api/spotify";
import client, { apiError } from "../../api/client";
import "./HomePage.scss";

const COMPOSERS = [
  "J.S. Bach",
  "Clara Schumann",
  "Debussy",
  "Ravel",
  "Florence Price",
  "Satie",
  "Rachmaninoff",
  "Hildegard von Bingen",
  "Chopin",
  "Philip Glass",
];

const SUGGESTIONS = ["Radiohead", "Adele", "Bon Iver", "Frank Ocean", "Hozier"];

export default function HomePage() {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [debouncedInput] = useDebounce(searchInput, 120);
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const [error, setError] = useState("");

  const cache = useRef(new Map()); // query -> results, for instant repeats
  const abortRef = useRef(null);

  // Warm the serverless function on arrival so the first search isn't cold.
  useEffect(() => {
    client.get("/").catch(() => {});
  }, []);

  useEffect(() => {
    const raw = debouncedInput.trim();
    const key = raw.toLowerCase();
    if (!raw) {
      abortRef.current?.abort();
      setResults([]);
      setStatus("idle");
      return;
    }

    // Instant: serve from cache with no network round-trip.
    if (cache.current.has(key)) {
      abortRef.current?.abort();
      setResults(cache.current.get(key));
      setStatus("done");
      return;
    }

    // Cancel any in-flight request so responses can't arrive out of order.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setStatus("loading");
    setError("");
    searchArtists(raw, controller.signal)
      .then((data) => {
        cache.current.set(key, data);
        setResults(data);
        setStatus("done");
      })
      .catch((err) => {
        if (err.code === "ERR_CANCELED" || err.name === "CanceledError") return;
        setError(apiError(err, "Search failed"));
        setStatus("error");
      });
  }, [debouncedInput]);

  // Show the results grid whenever there's a query; suggestions otherwise.
  const hasQuery = searchInput.trim().length > 0;

  // Enter / the Find button jumps straight to the strongest match.
  const handleSubmit = (e) => {
    e.preventDefault();
    const top = results[0];
    if (top) {
      navigate(`/quiz/results/${encodeURIComponent(top.name)}/${top.id}`);
    }
  };

  return (
    <div className="home">
      <div className="home__bg" aria-hidden="true">
        <div className="home__grain" />
      </div>

      <Header onDark />

      <main className="home__hero">
        <p className="home__eyebrow rise-1">
          <span>Classical music</span>
          <i />
          <span>for the curious</span>
        </p>

        <h1 className="home__title">
          <span className="rise-2">New journeys</span>
          <span className="home__title-em rise-3">through old melodies</span>
        </h1>

        <p className="home__lede rise-4">
          Name an artist you already love. We surface the classical pieces you
          didn't know you were missing — and tell you exactly why they'll land.
        </p>

        <div className="home__search rise-5">
          <form className="home__search-field" onSubmit={handleSubmit} role="search">
            <svg
              className="home__search-icon"
              viewBox="0 0 24 24"
              width="22"
              height="22"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
              <path d="M21 21l-4.3-4.3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <label htmlFor="artist-search" className="sr-only">
              Search for an artist
            </label>
            <input
              id="artist-search"
              type="text"
              className="home__search-input"
              placeholder="Search an artist you love…"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {searchInput && (
              <button
                type="button"
                className="home__search-clear"
                onClick={() => setSearchInput("")}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
            <button type="submit" className="home__search-go">
              Find
            </button>
          </form>
        </div>

        {hasQuery && (
          <div className="home__results" aria-live="polite">
              {status === "loading" && results.length === 0 && (
                <p className="home__results-msg">Searching…</p>
              )}

              {status === "error" && results.length === 0 && (
                <p className="home__results-msg">{error}</p>
              )}

              {status === "done" && results.length === 0 && (
                <p className="home__results-msg">
                  No artists found for “{debouncedInput.trim()}”. Try another name.
                </p>
              )}

              {results.length > 0 && (
                <div className={`home__grid${status === "loading" ? " is-loading" : ""}`}>
                  {results.slice(0, 8).map((artist, i) => (
                    <Link
                      key={artist.id}
                      to={`/quiz/results/${encodeURIComponent(artist.name)}/${artist.id}`}
                      className="home__artist"
                      style={{ "--i": i }}
                    >
                      <span className="home__artist-frame">
                        {artist.image ? (
                          <img
                            src={artist.image}
                            alt=""
                            className="home__artist-img"
                            loading="lazy"
                          />
                        ) : (
                          <span className="home__artist-ph">{artist.name[0]}</span>
                        )}
                        <span className="home__artist-go" aria-hidden="true">
                          →
                        </span>
                      </span>
                      <span className="home__artist-name">{artist.name}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}

        {!hasQuery && (
          <div className="home__try rise-6">
            <span className="home__try-label">Try</span>
            {SUGGESTIONS.map((name) => (
              <button
                key={name}
                type="button"
                className="home__try-chip"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setSearchInput(name);
                }}
              >
                {name}
              </button>
            ))}
          </div>
        )}
      </main>

      <div className="home__rule" aria-hidden="true">
        {COMPOSERS.slice(0, 6).map((name, i) => (
          <Fragment key={name}>
            {i > 0 && <i className="home__rule-sep">✦</i>}
            <span className="home__rule-item">{name}</span>
          </Fragment>
        ))}
      </div>
    </div>
  );
}
